import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminInquiriesApi } from "@/lib/commerce";
export default function AdminInquiries() {
  const [data, setData] = useState({ items: [], total: 0, pending_count: 0 });
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [updating, setUpdating] = useState({});

  const load = () => {
    setBusy(true);
    setErr("");
    const params = { page, page_size: 25 };
    if (tab === "pending") params.status_filter = "pending";
    if (tab === "handled") params.status_filter = "handled";
    adminInquiriesApi
      .list(params)
      .then(setData)
      .catch((e) => setErr(e.response?.data?.error?.message || "Failed to load."))
      .finally(() => setBusy(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [page, tab]);

  const toggle = async (row) => {
    const next = row.status === "handled" ? "pending" : "handled";
    setUpdating((u) => ({ ...u, [row.id]: true }));
    try {
      const updated = await adminInquiriesApi.setStatus(row.id, next);
      setData((d) => ({
        ...d,
        items: d.items.map((r) => (r.id === row.id ? updated : r)),
        pending_count: Math.max(0, d.pending_count + (next === "pending" ? 1 : -1)),
      }));
    } catch (e) {
      setErr(e.response?.data?.error?.message || "Update failed.");
    } finally {
      setUpdating((u) => ({ ...u, [row.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-inquiries-root">
      <header className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Inquiries</div>
            <div className="text-lg font-semibold flex items-center gap-2">
              Customer inquiries
              {data.pending_count > 0 && (
                <Badge variant="destructive" data-testid="admin-inquiries-pending-count">
                  {data.pending_count} pending
                </Badge>
              )}
            </div>
          </div>
          <Button asChild variant="outline"><Link to="/admin">&larr; Admin</Link></Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Inquiry log</CardTitle>
            <CardDescription>
              Every time a customer clicks the <b>Inquiry / Negotiate</b> button on a product page, a row
              lands here. Tick each one off once the salesman has replied.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {err && <div className="text-sm text-destructive">{err}</div>}
            <Tabs value={tab} onValueChange={(v) => { setPage(1); setTab(v); }}>
              <TabsList data-testid="admin-inquiries-tabs">
                <TabsTrigger value="all" data-testid="admin-inquiries-tab-all">All</TabsTrigger>
                <TabsTrigger value="pending" data-testid="admin-inquiries-tab-pending">Pending</TabsTrigger>
                <TabsTrigger value="handled" data-testid="admin-inquiries-tab-handled">Handled</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="text-sm text-muted-foreground">{data.total} shown</div>
            <div className="border rounded-lg overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-3 text-left">When</th>
                    <th className="p-3 text-left">Product</th>
                    <th className="p-3 text-left">Customer</th>
                    <th className="p-3 text-left">Link</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.id} className="border-t" data-testid={`inquiry-row-${r.id}`}>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{r.product_name || "—"}</div>
                        {r.product_sku && (
                          <div className="text-xs text-muted-foreground font-mono">{r.product_sku}</div>
                        )}
                        {r.note && (
                          <div className="text-[11px] text-muted-foreground mt-0.5">{r.note}</div>
                        )}
                      </td>
                      <td className="p-3">
                        {r.customer_name || r.customer_email ? (
                          <div>
                            {r.customer_name && <div>{r.customer_name}</div>}
                            {r.customer_email && (
                              <div className="text-xs text-muted-foreground">{r.customer_email}</div>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline">Guest</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        {r.page_url ? (
                          <a
                            href={r.page_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs underline underline-offset-2"
                          >
                            View page ↗
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        {r.status === "handled" ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            ✓ Handled
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-orange-300 text-orange-700">
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-right space-x-2 whitespace-nowrap">
                        {r.converted_to_order_number ? (
                          <Badge variant="secondary" data-testid={`inquiry-converted-${r.id}`}>
                            → {r.converted_to_order_number}
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!!updating[r.id]}
                            onClick={async () => {
                              if (!window.confirm(`Convert this enquiry into a real COD order for ${r.customer_name || 'this customer'}? An order will be created and linked to the enquiry.`)) return;
                              setUpdating((u) => ({ ...u, [r.id]: true }));
                              try {
                                const res = await adminInquiriesApi.convertToOrder(r.id);
                                setData((d) => ({
                                  ...d,
                                  items: d.items.map((x) => x.id === r.id ? { ...x, converted_to_order_number: res.order_number, status: "handled" } : x),
                                }));
                                window.alert(`Order ${res.order_number} created. Open Admin → Orders to continue.`);
                              } catch (e) {
                                const err = e.response?.data?.error;
                                setErr(err?.message || err?.details?.message || "Convert failed.");
                              } finally {
                                setUpdating((u) => ({ ...u, [r.id]: false }));
                              }
                            }}
                            data-testid={`inquiry-convert-${r.id}`}
                          >
                            Convert to order
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={r.status === "handled" ? "outline" : "default"}
                          disabled={!!updating[r.id]}
                          onClick={() => toggle(r)}
                          data-testid={`inquiry-toggle-${r.id}`}
                        >
                          {updating[r.id]
                            ? "…"
                            : r.status === "handled"
                              ? "Reopen"
                              : "Mark handled"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 && !busy && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                        No inquiries in this view.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {data.total > 25 && (
          <div className="flex justify-between items-center">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <div className="text-sm text-muted-foreground">Page {page}</div>
            <Button variant="outline" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}
      </main>
    </div>
  );
}
